import { useDeviceIdContext } from '@/components/device-id-provider';
import { Button } from '@/components/ui/button';
import { SessionEndButton } from '@/components/ui/session-end-button';
import { SessionSummary } from '@/components/ui/session-summary';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSessionSummary } from '@/hooks/useSessionSummary';
import { cn } from '@/lib/utils';
import { WavRecorder } from '@/lib/wavtools';
import { getCoachingInstructions } from '@/utils/coaching-instructions';
import { useAppApproach } from '@/contexts/AppModuleContext';
import api from '@/utils/ky';
import { StopIcon } from '@heroicons/react/24/outline';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Loader2,
  MessageSquare,
  Mic,
  MinusCircle,
  PlusCircle,
  Send,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Message } from './components/Message';

export function AppTextChat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('module');

  // Get device ID for API requests
  const { deviceId } = useDeviceIdContext();

  // Get current coaching approach
  const { currentApproach, approachConfig } = useAppApproach();

  // State for socket handling and conversation
  const [items, setItems] = useState<ItemType[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loadingState, setLoadingState] = useState('initial');
  const [isRecording, setIsRecording] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Session summary hook
  const {
    endSession,
    summaryData,
    clearSummary,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useSessionSummary();

  // Refs
  const clientRef = useRef<RealtimeClient | null>(null);
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 }),
  );
  const processedMessageIds = useRef<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(0);

  // Define interface for module data structure
  interface ModuleConfig {
    title: string;
    instructions: string;
  }

  // Module titles (instructions are now managed in coaching-instructions.ts)
  const moduleData: Record<string, ModuleConfig> = {
    'staying-centered': {
      title: 'Staying Centered When Things Get Tense',
      instructions: '', // Instructions are now managed by getCoachingInstructions
    },
    'breaking-free': {
      title: 'Breaking Free from Limiting Beliefs',
      instructions: '', // Instructions are now managed by getCoachingInstructions
    },
    'expressing-emotions': {
      title: 'Understanding and Expressing Emotions Constructively',
      instructions: '', // Instructions are now managed by getCoachingInstructions
    },
    'general-coaching': {
      title: 'General Coaching Session',
      instructions: '', // Instructions are now managed by getCoachingInstructions
    },
  };

  // Module state for dynamic updates
  const [moduleInfo, setModuleInfo] = useState<ModuleConfig>(() => {
    // For agile approach, find the module in approachConfig
    if (currentApproach === 'agile') {
      const agileModule = approachConfig.growthAreas.find(area => area.id === moduleId);
      if (agileModule) {
        const { instructions } = getCoachingInstructions(moduleId, currentApproach);
        return {
          title: agileModule.title,
          instructions: instructions,
        };
      }
    }

    // Default behavior for ontological or unknown modules
    const defaultModuleTitle =
      moduleData[moduleId as keyof typeof moduleData]?.title ||
      'Coaching Session';
    const { instructions } = getCoachingInstructions(moduleId, currentApproach);

    return {
      title: defaultModuleTitle,
      instructions: instructions,
    };
  });

  // Initialize conversation with OpenAI's RealtimeClient
  useEffect(() => {
    // Only initialize if we have a device ID
    if (deviceId) {
      initializeConversation();
    }

    return () => {
      const client = clientRef.current;
      if (client) {
        client.reset();
      }
    };
  }, [deviceId]); // Add deviceId as dependency

  // Initialize client
  const initializeClient = () => {
    return new RealtimeClient({
      url: import.meta.env.VITE_SOCKET_URI,
    });
  };

  // Initialize conversation
  const initializeConversation = async () => {
    try {
      setLoadingState('connecting');

      const client = await initializeClient();
      clientRef.current = client;

      startTimeRef.current = Date.now();
      setIsConnected(true);

      await client.connect();

      // Configure session
      const config: any = {
        input_audio_transcription: {
          model: 'gpt-4o-transcribe',
          language: 'en',
        },
        turn_detection: null,
        model: 'gpt-4o',
        voice: 'shimmer',
        temperature: 0.7,
      };
      client.updateSession(config);

      client.on('conversation.updated', async ({ item, delta }) => {
        const items = client.conversation.getItems();
        setItems(items);

        // Only process new completed messages
        if (item.status === 'completed') {
          const content =
            item.formatted?.transcript || item.formatted?.text || '';
          if (!content || !item.id) return;

          // Skip if we've already processed this message
          if (processedMessageIds.current.has(item.id)) {
            return;
          }

          // Mark message as processed
          processedMessageIds.current.add(item.id);
        }
      });

      // Define interface for API response
      interface InstructionsResponse {
        instructions: string;
        firstMessage: string;
        moduleId: string;
        moduleTitle: string;
        mode: 'text' | 'voice' | 'video';
        sessionId: string;
        coachName?: string;
        avatarColor?: string;
        voiceId?: string;
        avatarId?: string;
      }

      // Load module-specific instructions from backend
      try {
        // Ensure we have a device ID before making the request
        if (!deviceId) {
          console.error('Device ID is required but not available, using fallback');
          throw new Error('Device ID not available');
        }

        console.log('Making instructions request with device ID:', deviceId);

        // Attempt to fetch from backend with explicit device ID in headers
        const headers: Record<string, string> = {
          'x-device-id': deviceId,
          'Content-Type': 'application/json',
        };

        const response = await api
          .get(`api/app/modules/${moduleId}/instructions?mode=text`, {
            headers,
          })
          .json<InstructionsResponse>();

        let instructions = response.instructions;
        console.log('Instructions from backend:', response.instructions);
        console.log('First message from backend:', response.firstMessage);
        console.log('Session ID from backend:', response.sessionId);

        // Store sessionId from backend
        if (response.sessionId) {
          setSessionId(response.sessionId);
        } else {
          console.warn('No sessionId provided by backend, session end may fail');
        }

        // If we have a first message from the backend, we'll use it to start the conversation
        if (response.firstMessage) {
          instructions = `${instructions}\n\nSTART BY RESPONDING EXACTLY: ${response.firstMessage}`;
        }

        client.updateSession({ instructions });

        client.sendUserMessageContent([
          {
            type: 'input_text',
            text: 'Hello',
          },
        ]);
      } catch (err) {
        console.error('Error fetching instructions from backend:', err);
        // Fallback to local instructions using our custom coaching instructions
        console.log('Using local instructions fallback');
        const { instructions, firstMessage } = getCoachingInstructions(moduleId, currentApproach);

        // Append first message instruction to main instructions
        const fullInstructions = `${instructions}\n\nSTART BY RESPONDING EXACTLY: ${firstMessage}`;

        // Update session with our detailed instructions
        client.updateSession({ instructions: fullInstructions });

        // Use default prompt with local fallback
        client.sendUserMessageContent([
          {
            type: 'input_text',
            text: 'Hello',
          },
        ]);
      }

      startTimeRef.current = Date.now();
      setupClientListeners(client);
    } catch (error) {
      console.error('Connection error:', error);
      setError('Failed to connect to AI Coach');
    } finally {
      setLoadingState('ready');
    }
  };

  const setupClientListeners = (client: RealtimeClient) => {
    // Handle conversation updates
    client.on('conversation.updated', async ({ item, delta }) => {
      const items = client.conversation.getItems();
      setItems(items);
    });

    // Handle errors
    client.on('error', (error: any) => {
      console.error('Error Occurred', error);
      setError(error.message || 'An unexpected error occurred');
    });

    setItems(client.conversation.getItems());
  };

  // Audio recording functions
  const startRecording = async () => {
    if (isRecording) return;
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;

    // Begin a new audio recording session
    if (wavRecorder.getStatus() === 'ended') {
      // Asks for mic permission if not already granted
      await wavRecorder.begin();
    }

    await wavRecorder.record((data) => {
      if (client.isConnected()) {
        client.appendInputAudio(data.mono);
      }
    });
  };

  const stopRecording = async () => {
    if (!isRecording) return;
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();

    // End the recording session
    await wavRecorder.end();

    client.createResponse();
  };

  const handleTextInputSubmit = () => {
    if (textInput.trim() === '') return;
    sendMessage(textInput);
    setTextInput('');
  };

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setTextInput(event.target.value);
  };

  const sendMessage = async (message: string) => {
    const client = clientRef.current;
    if (client) {
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: message,
        },
      ]);
    }
  };

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [items]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(
      2,
      '0',
    )}`;
  };

  // Handle ending the session
  const handleEndSession = async () => {
    try {
      console.log('[AppTextChat] Starting session end process');

      // Collect messages for transcript
      const transcriptData = {
        messages: items.slice(1).map((item) => ({
          role: (item.role === 'assistant' ? 'coach' : 'user') as
            | 'user'
            | 'coach'
            | 'system',
          content: item?.formatted?.transcript || item?.formatted?.text || '',
          timestamp: new Date().toISOString(),
        })),
      };

      console.log('[AppTextChat] Transcript data collected:', transcriptData.messages.length, 'messages');

      if (!deviceId) {
        console.warn(
          'No device ID available. Session data may not be properly attributed.',
        );
      }

      // Request session summary with device ID
      const success = await endSession({
        sessionId: sessionId || 'text-session-' + Date.now(), // Use stored session ID or fallback
        moduleId: moduleId || 'general-coaching',
        sessionType: 'text',
        transcriptData,
        deviceId,
      });

      console.log('[AppTextChat] Session end result:', { success, summaryData, isSummaryLoading, summaryError });

      // Always show summary dialog - it will handle loading and error states internally
      console.log('[AppTextChat] Showing summary dialog');
      setShowSummary(true);

      // Clean up the session resources if successful
      if (success && clientRef.current) {
        clientRef.current.reset();
      }
    } catch (err) {
      console.error('[AppTextChat] Failed to end session:', err);
      // Show summary anyway - it will display appropriate error or fallback content
      setShowSummary(true);
    }
  };

  // Responsive layout handling
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [isInfoExpanded, setIsInfoExpanded] = useState(!isMobile);

  // Debug logging for summary state changes
  useEffect(() => {
    console.log('[AppTextChat] Summary state changed:', {
      showSummary,
      hasSummaryData: !!summaryData,
      isSummaryLoading,
      summaryError
    });
  }, [showSummary, summaryData, isSummaryLoading, summaryError]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Enhanced Header with Gradient */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-20 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between"
      >
        <div className="flex items-center max-w-full overflow-hidden">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/app')}
            className="mr-3 sm:mr-4 p-2 rounded-lg hover:bg-gray-100 flex-shrink-0 transition-colors"
            aria-label="Back to home"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </motion.button>
          <div className="flex flex-col overflow-hidden">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {moduleInfo.title}
            </h1>
            <div className="flex items-center">
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block w-2 h-2 rounded-full bg-primary mr-2"
              />
              <span className="text-xs sm:text-sm text-gray-600 font-medium">AI Coach Active</span>
            </div>
          </div>

          {isMobile && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsInfoExpanded(!isInfoExpanded)}
              className="ml-2 p-2 rounded-lg hover:bg-gray-100 flex-shrink-0 transition-colors"
              aria-label={isInfoExpanded ? 'Hide info' : 'Show info'}
            >
              {isInfoExpanded ? (
                <MinusCircle className="h-5 w-5 text-gray-600" />
              ) : (
                <PlusCircle className="h-5 w-5 text-gray-600" />
              )}
            </motion.button>
          )}
        </div>

        {/* Enhanced End session button */}
        <SessionEndButton
          onClick={handleEndSession}
          variant="ghost"
          className="text-xs sm:text-sm whitespace-nowrap text-red-600 hover:bg-red-50 border border-red-200 rounded-lg px-4 py-2 transition-colors"
        />
      </motion.header>

      {/* Enhanced Chat area with improved visual design */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
        <AnimatePresence>
          {items.slice(1).map((item, i) => {
            // Use slice(1) to skip the first message (the "Hello" message)
            // Skip empty system messages or hidden items
            if (item.role === 'system' || !item.formatted) {
              return null;
            }

            // Get message content with proper type safety
            const message =
              item?.formatted?.transcript || item?.formatted?.text || '';
            const isAssistant = item.role === 'assistant';

            // Skip empty messages
            if (!message.trim()) {
              return null;
            }

            return (
              <motion.div
                key={'message:' + i}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{
                  type: 'spring',
                  stiffness: 100,
                  damping: 20,
                  delay: i * 0.1,
                }}
                className="relative"
              >
                <Message
                  content={message}
                  sender={isAssistant ? 'coach' : 'user'}
                  timestamp={
                    new Date(
                      // Handle our synthetic messages first
                      item.id === 'ai-first-message'
                        ? // Use the timestamp we added to our synthetic message
                          (item as any).created_at
                        : // For real OpenAI messages, use the current time as fallback
                          Date.now(),
                    )
                  }
                />
              </motion.div>
            );
          })}

        </AnimatePresence>

        {/* Invisible element for scrolling to bottom */}
        <div ref={messagesEndRef} />
      </div>

      {/* Enhanced Input area with modern design */}
      {isConnected && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border-t border-gray-200 p-4 sm:p-6 sticky bottom-0 z-10"
        >
          <div className="flex items-end space-x-3 max-w-4xl mx-auto">
            <div className="flex-1 relative">
              {/* Enhanced Recording indicator */}
              {isRecording && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute -top-12 left-0 flex items-center justify-center"
                >
                  <div className="flex items-center space-x-3 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-200">
                    <motion.div
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.6, 1, 0.6],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      className="h-3 w-3 rounded-full bg-red-500"
                    />
                    <span className="text-sm font-medium">
                      Recording: {formatDuration(recordingTime)}
                    </span>
                  </div>
                </motion.div>
              )}

              <div className="relative">
                <textarea
                  value={textInput}
                  onChange={handleTextInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.shiftKey) {
                        return;
                      }
                      if (textInput.trim() !== '') {
                        handleTextInputSubmit();
                      }
                      e.preventDefault();
                    }
                  }}
                  placeholder="Share your thoughts with your AI coach..."
                  className="w-full bg-white border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-lg p-4 pr-24 resize-none outline-none min-h-[52px] text-sm sm:text-base transition-colors overflow-hidden"
                  rows={1}
                  style={{
                    height: Math.min(
                      isMobile ? 100 : 120,
                      Math.max(52, textInput.split('\n').length * 24 + 28)
                    ) + 'px',
                    maxHeight: isMobile ? '100px' : '120px'
                  }}
                  disabled={isRecording}
                />

                {/* Enhanced Control buttons */}
                <div className="absolute right-2 top-2 flex items-center space-x-1">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      className={cn(
                        'h-9 w-9 p-0 rounded-full transition-colors flex-shrink-0',
                        isRecording
                          ? 'bg-red-100 text-red-600 hover:bg-red-200'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                      )}
                      onClick={() => {
                        if (isRecording) {
                          stopRecording();
                        } else {
                          startRecording();
                        }
                      }}
                    >
                      {isRecording ? (
                        <StopIcon className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                    </Button>
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      size="sm"
                      className={cn(
                        'h-9 w-9 p-0 rounded-full transition-colors flex-shrink-0',
                        textInput.trim() === ''
                          ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                          : 'bg-primary hover:bg-primary/90 text-white',
                      )}
                      onClick={handleTextInputSubmit}
                      disabled={textInput.trim() === ''}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Enhanced Loading overlay with modern styling */}
      <AnimatePresence>
        {loadingState !== 'ready' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/95 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="text-center p-8 max-w-xs sm:max-w-sm mx-auto bg-white rounded-xl border border-gray-200"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="rounded-full bg-gray-100 w-20 h-20 mx-auto mb-6 flex items-center justify-center"
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Loader2 className="h-10 w-10 text-primary" />
                </motion.div>
              </motion.div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
                {loadingState === 'connecting'
                  ? 'Connecting to AI Coach'
                  : 'Loading Session'}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {loadingState === 'connecting'
                  ? 'Establishing a secure connection...'
                  : 'Preparing your personalized coaching session...'}
              </p>
              <motion.div
                className="mt-4 flex justify-center space-x-1"
              >
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 rounded-full bg-gray-400"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2
                    }}
                  />
                ))}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Session Summary Modal - improved responsiveness */}
      <AnimatePresence>
        {showSummary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-xl sm:max-w-2xl lg:max-w-4xl my-4"
            >
              <SessionSummary
                data={summaryData || {
                  title: 'Session Summary',
                  keyChallenge: 'Thank you for your session. The coach is analyzing your conversation.',
                  completionStatus: 'completed'
                }}
                onClose={() => {
                  setShowSummary(false);
                  clearSummary();
                  navigate('/app');
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
